import { baseApi } from './baseApi'

export const kcApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /knowledge-config — list KC items + formulas for the caller's org.
    // BFF response may double-wrap; transformResponse normalizes to a
    // consistent { knowledge_config_item, formulas } shape so consumers
    // always see plain arrays.
    getKnowledgeConfigItems: builder.query({
      query: () => ({ url: '/knowledge-config', method: 'GET' }),
      transformResponse: (response) => {
        const payload = response?.data ?? response ?? {}
        return {
          knowledge_config_item: Array.isArray(payload.knowledge_config_item)
            ? payload.knowledge_config_item
            : [],
          formulas: Array.isArray(payload.formulas) ? payload.formulas : [],
        }
      },
      providesTags: [{ type: 'KC', id: 'LIST' }],
    }),

    // POST /knowledge-config — create one item. Invalidates the list
    // so consumers re-fetch and see the new row.
    createKnowledgeConfigItem: builder.mutation({
      query: (payload) => ({
        url: '/knowledge-config',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: 'KC', id: 'LIST' }],
    }),

    // PUT /knowledge-config — update an existing item. Invalidates the
    // list + stats since stats are derived from items.
    updateKnowledgeConfigItem: builder.mutation({
      query: (payload) => ({
        url: '/knowledge-config',
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'KC', id: 'LIST' },
              { type: 'KCStats', id: 'LIST' },
            ],
    }),

    // DELETE /knowledge-config/:itemId?scope=user|org
    // Scope is a UI hint for the BFF's role gate (org=admin-only).
    deleteKnowledgeConfigItem: builder.mutation({
      query: ({ itemId, scope = 'user' }) => ({
        url: `/knowledge-config/${encodeURIComponent(itemId)}`,
        method: 'DELETE',
        params: { scope },
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'KC', id: 'LIST' },
              { type: 'KCStats', id: 'LIST' },
            ],
    }),

    // GET /knowledge-config/stats — per-item recall stats for the
    // caller's org. The legacy useKCStats hook returned a Map; we
    // return a plain object keyed by kc_item_id (immer-friendly,
    // serializable) plus the raw count.
    getKCStats: builder.query({
      query: () => ({ url: '/knowledge-config/stats', method: 'GET' }),
      transformResponse: (response) => {
        // BFF may wrap as { data: { stats, count } } or pass through
        // as { stats, count }; handle both.
        const payload = response?.data ?? response ?? {}
        const rows = Array.isArray(payload.stats) ? payload.stats : []
        const statsByKcItemId = {}
        for (const row of rows) {
          if (row && typeof row.kc_item_id === 'string' && row.kc_item_id) {
            statsByKcItemId[row.kc_item_id] = row
          }
        }
        return { statsByKcItemId, count: rows.length }
      },
      providesTags: [{ type: 'KCStats', id: 'LIST' }],
    }),

    // GET /knowledge-config/suggestions?limit=N — pending AI-generated
    // suggestions for the caller's org. Two real consumers:
    //   - DefaultProfilePopout: useGetSuggestionsQuery({ limit: 1 }) —
    //     cheap poll for the badge count.
    //   - ReviewQueueTab/SuggestionsList: useGetSuggestionsQuery({ limit: 50 })
    //     — the actual review surface.
    // Mutations below invalidate the Suggestions tag, so both cache
    // entries refresh on approve/reject/dismiss.
    getSuggestions: builder.query({
      query: ({ limit } = {}) => {
        const params = {}
        if (typeof limit === 'number') params.limit = limit
        return {
          url: '/knowledge-config/suggestions',
          method: 'GET',
          params,
        }
      },
      transformResponse: (response) => {
        const payload = response?.data ?? response ?? {}
        const suggestions = Array.isArray(payload.suggestions)
          ? payload.suggestions
          : []
        const count =
          typeof payload.count === 'number' ? payload.count : suggestions.length
        return { suggestions, count }
      },
      providesTags: [{ type: 'Suggestions', id: 'LIST' }],
    }),

    // POST /knowledge-config/suggestions/:memoryId/approve — materialise
    // a suggestion as a real KC item. Body mirrors KCCreateRequest (the
    // user may have edited the suggestion's fields before clicking
    // accept). Optimistically remove the row from the review-queue
    // cache; on success, KC items also need to refresh (the suggestion
    // is now a real KC row).
    approveSuggestion: builder.mutation({
      query: ({ memoryId, payload }) => ({
        url: `/knowledge-config/suggestions/${encodeURIComponent(memoryId)}/approve`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'Suggestions', id: 'LIST' },
              { type: 'KC', id: 'LIST' },
            ],
      async onQueryStarted(
        { memoryId, listArgs = { limit: 50 } },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          kcApi.util.updateQueryData('getSuggestions', listArgs, (draft) => {
            if (draft?.suggestions) {
              const before = draft.suggestions.length
              draft.suggestions = draft.suggestions.filter(
                (s) => s.id !== memoryId,
              )
              if (typeof draft.count === 'number') {
                draft.count -= before - draft.suggestions.length
              }
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    // POST /knowledge-config/suggestions/:memoryId/reject — mark
    // rejected. Same optimistic pattern.
    rejectSuggestion: builder.mutation({
      query: ({ memoryId, reason }) => ({
        url: `/knowledge-config/suggestions/${encodeURIComponent(memoryId)}/reject`,
        method: 'POST',
        data: { reason: reason || null },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: 'Suggestions', id: 'LIST' }],
      async onQueryStarted(
        { memoryId, listArgs = { limit: 50 } },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          kcApi.util.updateQueryData('getSuggestions', listArgs, (draft) => {
            if (draft?.suggestions) {
              const before = draft.suggestions.length
              draft.suggestions = draft.suggestions.filter(
                (s) => s.id !== memoryId,
              )
              if (typeof draft.count === 'number') {
                draft.count -= before - draft.suggestions.length
              }
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    // POST /knowledge-config/suggestions/:memoryId/dismiss — softer
    // "not now" rather than "no". Same optimistic pattern.
    dismissSuggestion: builder.mutation({
      query: ({ memoryId, reason }) => ({
        url: `/knowledge-config/suggestions/${encodeURIComponent(memoryId)}/dismiss`,
        method: 'POST',
        data: { reason: reason || null },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: 'Suggestions', id: 'LIST' }],
      async onQueryStarted(
        { memoryId, listArgs = { limit: 50 } },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          kcApi.util.updateQueryData('getSuggestions', listArgs, (draft) => {
            if (draft?.suggestions) {
              const before = draft.suggestions.length
              draft.suggestions = draft.suggestions.filter(
                (s) => s.id !== memoryId,
              )
              if (typeof draft.count === 'number') {
                draft.count -= before - draft.suggestions.length
              }
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetKnowledgeConfigItemsQuery,
  useCreateKnowledgeConfigItemMutation,
  useUpdateKnowledgeConfigItemMutation,
  useDeleteKnowledgeConfigItemMutation,
  useGetKCStatsQuery,
  useGetSuggestionsQuery,
  useApproveSuggestionMutation,
  useRejectSuggestionMutation,
  useDismissSuggestionMutation,
} = kcApi
